import React from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map, List } from 'immutable'
import { getBoundActions } from '../../actions/index'
import { formatDuration } from '../../theme/web-app/index'
import { getPrimary } from '../../services/languages/index'
import { CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT, getContentTags } from '../../services/content/index'
import { CLICK_TO_DETAILS_PAGE_GA4_EVENT, TAG_CLICKED_GA4_EVENT } from '../../services/event-tracking/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import ProgressBar from '../ProgressBar/index'
import Link from '../Link/index'
import { getOptimizedImageUrl } from './utils'

/**
 * DetailPageV3EpisodeItem component renders an individual episode item with image, title, tags,
 * and action buttons.
 * @param {Object} props - Component props
 * @param {Map} props.episode - Immutable Map containing episode data
 * @param {string} props.uiLocation - UI location identifier for analytics tracking
 * @param {boolean} props.isCurrentEpisode - Whether this episode is the currently active episode
 * @param {boolean} [props.showEpisodeNumber=true] - Whether to display the episode number prefix
 * @param {boolean} [props.showTags=true] - Whether to display content tags
 * @param {boolean} [props.showDuration=false] - Whether to display the episode duration
 * @param {Function} props.setEventDefault - Function to set default event tracking data
 * @param {Map} props.staticText - Immutable Map containing static text translations
 * @param {Map} props.user - Immutable Map containing user data
 * @returns {JSX.Element} Rendered episode item component
 */
const DetailPageV3EpisodeItem = (props) => {
  const {
    episode = Map(),
    uiLocation,
    isCurrentEpisode,
    showEpisodeNumber = true,
    showTags = true,
    showDuration = false,
    setEventDefault,
    staticText,
    user,
    setUpstreamContext,
    upstreamContext,
  } = props

  const ga4EventData = {
    content_name: episode.get('title'),
    content_id: episode.get('contentId'),
    content_type: episode.get('contentType'),
    media_id: episode.getIn(['featureMedia', 'mediaId']),
    media_language: episode.get('languageFullText'),
    parent_series_id: episode.get('seriesId'),
    parent_series_name: episode.get('seriesTitle'),
    is_featured_media: true,
    ui_location: uiLocation,
  }
  const userLanguage = getPrimary(user.getIn(['data', 'language']))

  const tags = showTags ? getContentTags({
    tags: episode.get('references', List()) || List(),
    url: episode.get('path'),
    contentSubtype: episode.get('contentSubtype'),
    isNew: episode.get('isNew'),
    contentType: episode.get('contentType'),
    staticText,
    userLanguage,
    displayType: episode.get('displayType'),
  }) || List() : List()
  const duration = episode.getIn(['featureMedia', 'duration'], 0) || 0
  const userMediaPosition = episode.getIn(['featureMedia', 'userMediaPosition', 'position'], 0) || 0
  const hasProgressBar = userMediaPosition > 0
  const image = episode.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT) || Map()
  const visibleTags = tags.slice(0, 2)
  const { fallbackImageUrl, imageUrl } = getOptimizedImageUrl(image.get('url'), '150')

  const handleLinkClick = () => {
    setUpstreamContext(upstreamContext)

    // GA4 events
    const clickToDetailsPageEventData = CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep({
      ...ga4EventData,
    })
    setEventDefault(clickToDetailsPageEventData)
  }

  const handleTagClickGaEvent = () => {
    const clickToDetailsPageEventData = TAG_CLICKED_GA4_EVENT.mergeDeep({
      ...ga4EventData,
    })
    setEventDefault(clickToDetailsPageEventData)
  }

  const className = [
    'detail-page-v3__episode-item',
    isCurrentEpisode ? 'detail-page-v3__episode-item--current' : null,
  ].filter((cls) => !!cls).join(' ')

  if (!episode.get('contentId')) {
    return <div className={className}>
      <div className="detail-page-v3__episode-item-placeholder" />
    </div>
  }

  return (
    <div className={className}>
      <div className="detail-page-v3__episode-item-container">
        <div className="detail-page-v3__episode-item-image">
          <Link to={episode.get('path')} onClick={handleLinkClick}>
            <Picture
              fallbackSrc={fallbackImageUrl}
              alt={episode.get('title')}
              loading={IMAGE_LOADING_LAZY}
              sources={[
                {
                  type: IMAGE_TYPE_WEBP,
                  srcSet: imageUrl,
                },
              ]}
            />
            {hasProgressBar && (
              <div className="detail-page-v3__episode-item-progress-bar">
                <ProgressBar
                  duration={duration}
                  userInfo={Map({ featurePosition: userMediaPosition })}
                />
              </div>
            )}
          </Link>
        </div>
        <div className="detail-page-v3__episode-item-details">
          <Link to={episode.get('path')} onClick={handleLinkClick}>
            <h5 className="detail-page-v3__episode-item-title">
              {showEpisodeNumber && `${episode.get('episodeNumber')}. `}{episode.get('title')}
            </h5>
          </Link>
          {showTags && (
            <div className="detail-page-v3__episode-item-tags">
              {visibleTags.map((t) => (
                <Link key={t.get('contentId')} to={t.get('path')} onClick={handleTagClickGaEvent}>
                  {t.get('name') || t.get('title')}
                </Link>
              ))}
            </div>
          )}
          {showDuration && (
            <p className="detail-page-v3__episode-item-duration">
              {formatDuration(duration)}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default connectRedux((state) => ({
  user: state.user,
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
    setUpstreamContext: actions.upstreamContext.setUpstreamContext,
  }
})(DetailPageV3EpisodeItem)
